/** Small pure helpers for VFX placement (sim plane coordinates, yards). */
import type { WorldShape } from '@keystone/sim';

/** Uniform-ish random point inside a world shape. `r1`, `r2` in [0, 1). Writes into `out`. */
export function samplePointInShape(a: WorldShape, r1: number, r2: number, out: { x: number; y: number }): { x: number; y: number } {
  const s = a.shape;
  let lx = 0;
  let ly = 0;
  switch (s.type) {
    case 'circle': {
      const d = Math.sqrt(r1) * s.radius;
      const t = r2 * Math.PI * 2;
      lx = Math.cos(t) * d;
      ly = Math.sin(t) * d;
      break;
    }
    case 'ring': {
      const d = Math.sqrt(s.inner * s.inner + r1 * (s.outer * s.outer - s.inner * s.inner));
      const t = r2 * Math.PI * 2;
      lx = Math.cos(t) * d;
      ly = Math.sin(t) * d;
      break;
    }
    case 'cone': {
      const d = Math.sqrt(r1) * s.range;
      const t = (r2 - 0.5) * Math.min(Math.PI * 2, s.angle);
      lx = Math.cos(t) * d;
      ly = Math.sin(t) * d;
      break;
    }
    case 'line':
      lx = r1 * s.length;
      ly = (r2 - 0.5) * s.width;
      break;
    case 'rect':
      lx = (r1 - 0.5) * s.h;
      ly = (r2 - 0.5) * s.w;
      break;
  }
  const c = Math.cos(a.facing);
  const n = Math.sin(a.facing);
  out.x = a.origin.x + lx * c - ly * n;
  out.y = a.origin.y + lx * n + ly * c;
  return out;
}

/** Rough area of a shape in square yards (scales particle counts). */
export function shapeArea(a: WorldShape): number {
  const s = a.shape;
  switch (s.type) {
    case 'circle':
      return Math.PI * s.radius * s.radius;
    case 'ring':
      return Math.PI * (s.outer * s.outer - s.inner * s.inner);
    case 'cone':
      return 0.5 * Math.min(Math.PI * 2, s.angle) * s.range * s.range;
    case 'line':
      return s.length * s.width;
    case 'rect':
      return s.w * s.h;
  }
}

/** Centroid-ish point of a shape (where the slam "lands"). */
export function shapeCenter(a: WorldShape, out: { x: number; y: number }): { x: number; y: number } {
  const s = a.shape;
  let d = 0;
  if (s.type === 'cone') d = s.range * 0.5;
  else if (s.type === 'line') d = s.length * 0.5;
  out.x = a.origin.x + Math.cos(a.facing) * d;
  out.y = a.origin.y + Math.sin(a.facing) * d;
  return out;
}

export const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;
export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
